import refusal from './letters-signing-refusals-zh'

/**
 * 签名证书（planning P4-3b D-587 至 D-589）：设置 → 规划 → 签名证书、函件面板的“在 gisify 中签名”（`panel.*`）
 * 和持有人自己的列表（`mine.*`）。真正的中文译文。
 */
export default {
  title: '签名证书',
  intro:
    'gisify 可以用本市政局保存在此的证书签署已发出的函件。这种签名向 gisify 以外的人证明的是：函件自签名后未被更改，并且是用本市政局的证书签署的。市政局根证书下的每个证书都与根证书署名相同的机构：即创建根证书时信头上的市政局名称。尚未信任市政局根证书的读者会看到签署人为未知。签名中不含时间戳或吊销数据。侵入 gisify 的人可以冒市政局之名签署函件。',
  root: {
    title: '市政局根证书',
    create: '创建市政局根证书',
    help: '请把此证书交给需要核验您函件的人。他们只需在 Adobe Reader 的“受信任证书”中添加一次，并只用于签名。绝不可将它添加到电脑或浏览器的根证书存储中。侵入 gisify 的人可以用它冒本市政局之名签署函件。',
  },
  download: { cer: '下载（.cer）', pem: '下载（.pem）' },
  fingerprint: 'SHA-256 指纹',
  issue: { title: '签发签名证书', submit: '签发' },
  upload: {
    title: '上传市政局印章证书',
    sealOnly: '管理员在此上传市政局印章证书。个人证书由证书上署名的人本人在规划列表的“我的签名证书”中上传。',
    submit: '上传',
  },
  fields: {
    label: '标签',
    commonName: '证书上的名称',
    organization: '机构',
    organizationFixed:
      '此根证书下的每个证书都署名为 {{council}}，即创建根证书时信头上的市政局。如需更改，请删除根证书并创建新的根证书。',
    commonNameRootDefault:
      '留空则使用市政局名称加上“Letter Root”；如果这样会超过 64 个字符，则只用市政局名称。您也可以输入更短的名称。',
    organizationalUnit: '部门（可选）',
    country: '国家代码（可选，例如 MY）',
    copyFromSignatory: '复制签署人的姓名',
    binding: '谁可以使用',
    person: '人员',
    format: { pkcs12: 'PKCS#12 文件（.p12、.pfx）', pem: 'PEM 密钥和证书链' },
    password: '文件密码',
    passwordNote: '密码只用于打开文件一次，随后即从 gisify 中移除。数据库备份中的副本随备份一起过期。',
    file: '文件',
    keyFile: '私钥（.pem、.key）',
    chainFile: '证书和证书链（.pem、.crt）',
    keyPassword: '密钥密码（如有）',
  },
  binding: { personal: '仅限此人', seal: '任何可以签署该函件的官员（市政局印章）' },
  kind: { root: '市政局根证书', signing: '签名证书' },
  source: { generated: '在 gisify 中生成', uploaded: '已上传' },
  approve: {
    title: '批准个人证书',
    selfSigned: '自签名',
    body: '只有在名称确实是该官员本人的、且指纹与该官员提供的一致时才批准。批准后，用此证书签署的函件在 gisify 中受信任，并可自动共享。停用证书会撤销批准。',
    commonName: '与函件签署人比较的名称',
    subject: '完整主体',
    nameNone: '此证书没有可供 gisify 比较的名称。',
    nameMoreThanOne: '此证书有多个名称。',
    nameUnclear: '不能批准：gisify 只与签署人比较恰好一个名称。',
    subjectCut: '完整主体比 gisify 显示的更长。请根据上方的名称作出决定，而不是主体的末尾。',
    hiddenCharacters:
      '名称或主体中含有不可见或改变文字方向的字符，此处以代码显示。除非您知道它们为何存在，否则不要批准。',
  },
  actions: {
    approve: '批准',
    disable: '停用',
    enable: '启用',
    delete: '删除',
    cancel: '取消',
    refresh: '再次检查',
  },
  confirm: {
    delete:
      '删除此证书？其私钥将从 gisify 中移除，此后再也不能签名。数据库备份中的副本随备份一起过期。已用它签署的函件不受影响。',
    deleteRoot: '删除根证书时，它签发的每个签名证书也会一并删除。',
    disableApproved: '停用此证书也会撤销其批准。如果再次启用，管理员必须重新批准。',
  },
  warning: {
    holderNotQualified: '此证书绑定的人员已不能签署函件，因此用它签署的函件不受信任。请停用或删除它。',
    chainIncomplete: '文件未包含直到根证书的完整证书链。gisify 以外的读者可能无法核验此证书。',
    icNumber: '此证书带有身份证号码。用它签署的每封函件在共享前都需要官员确认。',
  },
  limits:
    '来自 Adobe 认可信任列表（AATL）上 CA 的证书，以及马来西亚的令牌或漫游证书，其密钥保存在设备上，无法上传。请用您自己的工具签署这些函件，再上传已签名的文件。',
  status: {
    pending: '正在准备…',
    pendingSlow: '所需时间比预期更长。',
    active: '就绪',
    disabled: '已停用',
    deleted: '已删除：其密钥已从 gisify 中移除',
    refused: '未被接受',
    expired: '已过期',
    notYetValid: '自 {{date}} 起生效',
    awaitingApproval: '可以签名。在管理员批准之前，函件需等待官员处理。',
  },
  expires: '{{date}} 到期',
  expiringSoon: '{{days}} 天后到期',
  folded: '已删除和未被接受（{{count}}）',
  problems: {
    required: '请填写此项。',
    tooLong: '最多 {{max}} 个字符。',
    control: '请删除控制字符。',
    country: '请使用两个大写字母，例如 MY。',
    fileMissing: '请选择文件。',
    fileTooLarge: '文件大于 {{max}} KiB。',
    person: '请选择人员。',
  },
  panel: {
    button: '在 gisify 中签名',
    title: '在 gisify 中签署函件',
    choose: '选择证书',
    what: 'gisify 用此证书签署已发出的函件，然后像核验上传的文件一样核验它。',
    sealNote: '市政局印章证书不署签署人的姓名，因此函件需等待官员共享。',
    unapprovedNote: '管理员尚未批准此证书，因此函件需等待官员共享。',
    sign: '签名',
    signedWith: '已于 {{time}} 在 gisify 中用 {{label}} 签名',
    binding: { personal: '个人证书', seal: '市政局印章证书' },
    refusal: {
      signing_certificate_unusable:
        'gisify 未签署此函件：所选证书已不能签名。请选择其他证书，或用您自己的工具签署已发出的文件后上传。',
      signing_key_unreadable: 'gisify 未签署此函件：无法打开该证书的私钥。需由管理员重新签发或上传该证书。',
      signing_failed: 'gisify 无法用该证书签署此函件。请重试；如再次发生，请告知管理员。',
      unknown: 'gisify 无法签署此函件。请重试，或用您自己的工具签署已发出的文件后上传。',
      dispatch_failed: 'gisify 无法开始签署此函件。请再次点击“在 gisify 中签名”。',
      verify_timeout: '签名和核验耗时过长，已被停止。请再次点击“在 gisify 中签名”。',
    },
  },
  mine: {
    title: '我的签名证书',
    open: '我的签名证书',
    intro:
      '只有您本人可以上传和使用绑定到您的个人证书。其他任何人，包括管理员，都不能替您上传。管理员批准后，用它签署的函件才能自动共享。',
    upload: '上传我的证书',
    empty: '您还没有签名证书。',
    close: '关闭',
  },
  refusal,
}
